import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { IProduct } from '../types/Store';
import { useStore } from './StoreContext';
import { TrackingService } from '../services/TrackingService';
import { useToast } from './ToastContext';
import { Environment } from '../environment';

export interface ICartItem {
    id: string; // ID único para o item no carrinho (pode ser product.id + options hash)
    product: IProduct;
    quantity: number;
    options?: any; // Para molhos, acompanhamentos, etc.
}

interface ICartContextData {
    items: ICartItem[];
    addItem: (product: IProduct, quantity: number, options?: any) => void;
    removeItem: (itemId: string) => void;
    duplicateItem: (itemId: string) => void;
    updateQuantity: (itemId: string, quantity: number, options?: any) => void;
    clearCart: () => void;
    applyCupom: (code: string, phone?: string, isComanda?: boolean) => Promise<void>;
    removeCupom: () => void;
    appliedCupom: string | null;
    discountAmount: number;
    promotionDiscount: number;
    subtotal: number;
    total: number;
    deliveryFee: number;
    setDeliveryFee: (fee: number) => void;
}

const CartContext = createContext<ICartContextData>({} as ICartContextData);

export const useCart = () => useContext(CartContext);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { store, products, flavors, additionals, sauces, sideDishes } = useStore();
    const { showToast } = useToast();
    const [items, setItems] = useState<ICartItem[]>([]);
    const [deliveryFee, setDeliveryFee] = useState(0);
    const [recoveryAttempted, setRecoveryAttempted] = useState(false);
    
    const [appliedCupom, setAppliedCupom] = useState<string | null>(null);
    const [cupomInfo, setCupomInfo] = useState<{ type: 'percentage' | 'fixed', value: number } | null>(null);

    // Carregar carrinho do localStorage ao iniciar
    useEffect(() => {
        if (store) {
            const savedCart = localStorage.getItem(`cart_${store.id}`);
            if (savedCart) {
                try {
                    setItems(JSON.parse(savedCart));
                } catch (_) {}
            }
            setDeliveryFee(store.deliveryFee || 0);
        }
    }, [store]);

    const subtotal = useMemo(() => {
        return items.reduce((acc, item) => {
            // Preço base do produto - Tentamos pegar o preço original do catálogo para evitar bitributação
            const originalProduct = products.find(p => (p.id || (p as any)._id) === (item.product.id || (item.product as any)._id));
            let itemBasePrice = originalProduct ? originalProduct.price : item.product.price;
            
            let optionsPrice = 0;

            // Somar preços dos sabores selecionados
            if (item.options?.flavors && Array.isArray(item.options.flavors)) {
                item.options.flavors.forEach((flavorName: string) => {
                    const flavor = flavors.find(f => f.name === flavorName);
                    if (flavor && flavor.price > 0) {
                        optionsPrice += flavor.price;
                    }
                });
            }

            // Somar preços dos adicionais selecionados
            if (item.options?.additionals && Array.isArray(item.options.additionals)) {
                item.options.additionals.forEach((additionalName: string) => {
                    const additional = additionals.find(a => a.name === additionalName);
                    if (additional && additional.price > 0) {
                        optionsPrice += additional.price;
                    }
                });
            }

            // Somar preços dos molhos selecionados
            if (item.options?.sauces && Array.isArray(item.options.sauces)) {
                item.options.sauces.forEach((sauceName: string) => {
                    const sauce = sauces.find(s => s.name === sauceName);
                    if (sauce && sauce.price > 0) {
                        optionsPrice += sauce.price;
                    }
                });
            }

            // Somar preço dos acompanhamentos selecionados
            if (item.options?.sideDishes && Array.isArray(item.options.sideDishes)) {
                item.options.sideDishes.forEach((sideDishName: string) => {
                    const sideDish = sideDishes.find(s => s.name === sideDishName);
                    if (sideDish && sideDish.price > 0) {
                        optionsPrice += sideDish.price;
                    }
                });
            }

            // Preço variável por tamanho: o tamanho selecionado define o preço base
            if ((originalProduct as any)?.hasVariableSizePrice && item.options?.size) {
                const szList = (originalProduct as any).sizes || [];
                const sz = szList.find((s: any) => s.name === item.options?.size);
                if (sz) itemBasePrice = sz.price;
            }

            // Somar extras de combos a partir das seleções por slot
            if (item.product.isCombo && item.options?.comboSelections) {
                Object.values(item.options.comboSelections).forEach((sel: any) => {
                    (sel.flavors || []).forEach((name: string) => {
                        const f = flavors.find(x => x.name === name);
                        if (f && (f as any).price > 0) optionsPrice += (f as any).price;
                    });
                    (sel.sauces || []).forEach((name: string) => {
                        const s = sauces.find(x => x.name === name);
                        if (s && (s as any).price > 0) optionsPrice += (s as any).price;
                    });
                    (sel.sideDishes || []).forEach((name: string) => {
                        const s = sideDishes.find(x => x.name === name);
                        if (s && (s as any).price > 0) optionsPrice += (s as any).price;
                    });
                    Object.entries(sel.additionalQtys || {}).forEach(([name, qty]) => {
                        const a = additionals.find(x => x.name === name);
                        if (a && (a as any).price > 0) optionsPrice += (a as any).price * (qty as number);
                    });
                });
            }

            return acc + ((itemBasePrice + optionsPrice) * item.quantity);
        }, 0);
    }, [items, products, flavors, additionals, sauces, sideDishes]);

    const discountAmount = useMemo(() => {
        if (!cupomInfo) return 0;
        if (cupomInfo.type === 'fixed') return cupomInfo.value;
        return (subtotal * cupomInfo.value) / 100;
    }, [subtotal, cupomInfo]);

    const promotionDiscount = useMemo(() => {
        const now = new Date();
        return items.reduce((acc, item) => {
            const promo = (item.product as any).promotion;
            if (!promo?.active || !promo.value) return acc;
            if (promo.expirationDate && new Date(promo.expirationDate) < now) return acc;
            const originalProduct = products.find(p => (p.id || (p as any)._id) === (item.product.id || (item.product as any)._id));
            const basePrice = originalProduct ? originalProduct.price : item.product.price;
            const itemDiscount = promo.type === 'percentage'
                ? (basePrice * promo.value / 100)
                : Math.min(promo.value, basePrice);
            return acc + itemDiscount * item.quantity;
        }, 0);
    }, [items, products]);

    const total = useMemo(() => {
        return Math.max(0, subtotal - discountAmount - promotionDiscount) + deliveryFee;
    }, [subtotal, discountAmount, promotionDiscount, deliveryFee]);

    const applyCupom = useCallback(async (code: string, phone?: string, isComanda?: boolean) => {
        if (!store) return;
        
        try {
            const response = await fetch(`${Environment.API_URL}/cupons/validate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    storeId: store.id,
                    code,
                    phone,
                    isComanda
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Erro ao validar cupom');
            }

            setAppliedCupom(data.cupom.code);
            setCupomInfo({ type: data.cupom.type, value: data.cupom.value });
            showToast('Cupom aplicado com sucesso!', 'success');
        } catch (error: any) {
            showToast(error.message, 'error');
            throw error;
        }
    }, [store, showToast]);

    const removeCupom = useCallback(() => {
        setAppliedCupom(null);
        setCupomInfo(null);
        showToast('Cupom removido');
    }, [showToast]);

    // Salvar carrinho no localStorage e Tracking quando mudar
    useEffect(() => {
        if (store) {
            localStorage.setItem(`cart_${store.id}`, JSON.stringify(items));

            // Tracking de Carrinho Abandonado
            if (items.length > 0) {
                const timer = setTimeout(() => {
                    TrackingService.saveAbandonedCart(store.id, {
                        items: items.map(i => ({
                            productId: i.product.id,
                            quantity: i.quantity,
                            name: i.product.name,
                            price: i.product.price,
                            options: i.options ?? null,
                            flavors:            i.options?.flavors            ?? [],
                            additionals:        i.options?.additionals        ?? [],
                            sauces:             i.options?.sauces             ?? [],
                            sideDishes:         i.options?.sideDishes         ?? [],
                            salads:             i.options?.salads             ?? [],
                            desserts:           i.options?.desserts           ?? [],
                            removedIngredients: i.options?.removedIngredients ?? [],
                            observations:       i.options?.observations       ?? '',
                        })),
                        subtotal,
                        total,
                    });
                }, 2000);

                return () => clearTimeout(timer);
            }
        }
    }, [items, store, subtotal, total]);

    // Recuperação de carrinho abandonado via URL
    useEffect(() => {
        const processRecovery = async () => {
            if (!store || !products || products.length === 0 || recoveryAttempted) {
                return;
            }

            // Se o usuário já está em uma comanda aberta, ignora recuperação automática de carrinho abandonado
            const activeComanda = sessionStorage.getItem('comanda_phone');
            if (activeComanda) {
                setRecoveryAttempted(true);
                return;
            }

            const recoveryToken = TrackingService.checkRecoveryToken();
            if (!recoveryToken) {
                setRecoveryAttempted(true);
                return;
            }

            setRecoveryAttempted(true);

            try {
                const recoveredCart = await TrackingService.recoverCart(recoveryToken, store.id);
                
                if (!recoveredCart || !recoveredCart.items || recoveredCart.items.length === 0) {
                    showToast('Não foi possível recuperar o carrinho', 'error');
                    return;
                }

                // Mapear items recuperados para produtos do catálogo
                const restoredItems: ICartItem[] = [];
                
                for (const item of recoveredCart.items) {
                    const product = products.find((p: IProduct) =>
                        String(p.id) === String(item.productId || item.id)
                    );

                    if (product) {
                        // Suporta qty (AI agent) e quantity (client frontend)
                        const quantity = item.quantity || item.qty || 1;

                        // Suporta options aninhado (client) e arrays flat (AI agent)
                        const hasFlat = item.flavors?.length || item.additionals?.length ||
                            item.sauces?.length || item.sideDishes?.length ||
                            item.salads?.length || item.desserts?.length ||
                            item.removedIngredients?.length || item.observations;
                        const options = item.options
                            ? item.options
                            : hasFlat
                            ? {
                                flavors: item.flavors || [],
                                additionals: item.additionals || [],
                                sauces: item.sauces || [],
                                sideDishes: item.sideDishes?.length ? item.sideDishes : item.sideDish ? [item.sideDish] : [],
                                salads: item.salads || [],
                                desserts: item.desserts || [],
                                removedIngredients: item.removedIngredients || [],
                                observations: item.observations || '',
                            }
                            : undefined;

                        const itemId = `${product.id}_${JSON.stringify(options || {})}`;
                        restoredItems.push({
                            id: itemId,
                            product: product,
                            quantity,
                            options
                        });
                    }
                }

                if (restoredItems.length > 0) {
                    setItems(restoredItems);
                    sessionStorage.setItem('isRecoveredSession', 'true');
                    showToast(`Carrinho recuperado com sucesso! ${restoredItems.length} itens restaurados`, 'success');

                    // Limpar parâmetro da URL
                    const url = new URL(window.location.href);
                    url.searchParams.delete('recovery');
                    window.history.replaceState({}, '', url.toString());
                } else {
                    showToast('Os produtos do carrinho não estão mais disponíveis', 'error');
                }

            } catch (_) {
                showToast('Erro ao recuperar carrinho', 'error');
            }
        };

        processRecovery();
    }, [store, products, recoveryAttempted, showToast]);

    const addItem = useCallback((product: IProduct, quantity: number, options?: any) => {
        setItems(prev => {
            const timestamp = Date.now();
            const itemId = `${product.id}_${timestamp}`;

            return [...prev, { id: itemId, product, quantity, options }];
        });
    }, []);

    const removeItem = useCallback((itemId: string) => {
        setItems(prev => prev.filter(item => item.id !== itemId));
    }, []);

    const duplicateItem = useCallback((itemId: string) => {
        setItems(prev => {
            const item = prev.find(i => i.id === itemId);
            if (!item) return prev;

            const hasOptions = item.options && Object.keys(item.options).some(key => {
                const val = item.options[key];
                if (Array.isArray(val)) return val.length > 0;
                if (typeof val === 'string') return val.trim().length > 0;
                return !!val;
            });

            if (hasOptions) {
                const timestamp = Date.now();
                const newItemId = `${item.product.id}_${timestamp}`;
                return [...prev, { ...item, id: newItemId, quantity: 1 }];
            } else {
                return prev.map(i => i.id === itemId ? { ...i, quantity: i.quantity + 1 } : i);
            }
        });
    }, []);

    const updateQuantity = useCallback((itemId: string, quantity: number, options?: any) => {
        if (quantity <= 0) {
            removeItem(itemId);
            return;
        }
        setItems(prev => prev.map(item => item.id === itemId ? { ...item, quantity, options: options || item.options } : item));
    }, [removeItem]);

    const clearCart = useCallback(() => {
        setItems([]);
        setAppliedCupom(null);
        setCupomInfo(null);
        if (store) {
            localStorage.removeItem(`cart_${store.id}`);
        }
    }, [store]);

    return (
        <CartContext.Provider value={{
            items,
            addItem,
            removeItem,
            duplicateItem,
            updateQuantity,
            clearCart,
            applyCupom,
            removeCupom,
            appliedCupom,
            discountAmount,
            promotionDiscount,
            subtotal,
            total,
            deliveryFee,
            setDeliveryFee
        }}>
            {children}
        </CartContext.Provider>
    );
};
